import { useEffect, useRef, useState } from "react"
import { adminKeys } from "../../search-standalone/services/adminKeys"
import { ShortlistJourneySettings } from "./ShortlistJourneySettings"
import { ShortlistLaunchLinkSettings } from "./ShortlistLaunchLinkSettings"

interface State {
	selectionLimits?: { percentage: number; maxProducts: number | null }
	enabled: boolean
	status: string
	selectedCount?: number
	catalogueCount?: number
	lastRefreshedAt?: number
	error?: string
	activeGeneration?: string
	manualProductIds?: string[]
	crossBrandDiscovery?: boolean
}
export function ShortlistSettings({
	storeUrl,
	getAuthHeader,
}: {
	storeUrl: string
	getAuthHeader: () => Promise<Record<string, string>>
}) {
	const [slug, setSlug] = useState("")
	const [state, setState] = useState<State>()
	const [busy, setBusy] = useState(false)
	const [error, setError] = useState("")
	const [preview, setPreview] = useState<Array<{ title: string; rank: number }>>([])
	const [candidates, setCandidates] = useState<
		Array<{
			id: string
			title: string
			url?: string
			gpt_readiness?: { status: string; reason: string | null; description_bytes: number }
		}>
	>([])
	const [manualIds, setManualIds] = useState<string[]>([])
	const [cap, setCap] = useState(20)
	const [candidatePage, setCandidatePage] = useState(1)
	const [candidateTotal, setCandidateTotal] = useState(0)
	const [editing, setEditing] = useState(false)
	const [candidateQuery, setCandidateQuery] = useState("")
	const [percentage, setPercentage] = useState("20")
	const [maximum, setMaximum] = useState("20")
	const limitsDirty = useRef(false)
	const base = `${import.meta.env.VITE_BACKEND_URL || ""}/api/v1/search/shortlist`
	async function request(url: string, method = "GET", body?: unknown) {
		const response = await fetch(url, {
			method,
			headers: {
				"Content-Type": "application/json",
				...(await getAuthHeader()),
				...(await adminKeys.authHeaders()),
			},
			...(body ? { body: JSON.stringify(body) } : {}),
		})
		const result = await response.json()
		if (!response.ok) throw new Error(result.error || "Shortlist request failed")
		return result
	}
	useEffect(() => {
		let stopped = false
		setState(undefined)
		setSlug("")
		setError("")
		setPreview([])
		setEditing(false)
		setCandidates([])
		limitsDirty.current = false
		setPercentage("20")
		setMaximum("20")
		if (!storeUrl) return
		const load = async () => {
			try {
				const result = await request(`${base}/participation?storeUrl=${encodeURIComponent(storeUrl)}`)
				if (!stopped) {
					setSlug(result.storeSlug)
					setState(result.participation)
					if (!limitsDirty.current) {
						const limits = result.participation.selectionLimits || { percentage: 20, maxProducts: 20 }
						setPercentage(String(limits.percentage))
						setMaximum(limits.maxProducts === null ? "" : String(limits.maxProducts))
					}
					setError("")
				}
			} catch (e: any) {
				if (!stopped) setError(e.message)
			}
		}
		void load()
		const timer = setInterval(() => {
			if (!document.hidden) void load()
		}, 10000)
		return () => {
			stopped = true
			clearInterval(timer)
		}
	}, [storeUrl])
	async function act(action: "toggle" | "discovery" | "preview" | "refresh" | "refresh-catalogue") {
		setBusy(true)
		setError("")
		try {
			if (action === "toggle") {
				const result = await request(`${base}/stores/${slug}`, "PUT", {
					enabled: !state?.enabled,
				})
				setState(result.participation)
			} else if (action === "discovery") {
				const result = await request(`${base}/stores/${slug}/discovery`, "PUT", {
					enabled: !state?.crossBrandDiscovery,
				})
				setState(result.participation)
			} else if (action === "preview") {
				const result = await request(`${base}/stores/${slug}/preview`, "POST")
				setPreview(result.products)
			} else {
				await request(
					`${base}/stores/${slug}/${action === "refresh-catalogue" ? "refresh-catalogue" : "refresh"}`,
					"POST",
				)
				setState((s) => (s ? { ...s, status: "queued" } : s))
			}
		} catch (e: any) {
			setError(e.message)
		} finally {
			setBusy(false)
		}
	}
	async function loadCandidates(page = 1) {
		setBusy(true)
		setError("")
		try {
			const result = await request(
				`${base}/stores/${slug}/selection-candidates?page=${page}&query=${encodeURIComponent(candidateQuery)}`,
			)
			if (!editing) setManualIds(state?.manualProductIds || [])
			setCandidates(result.products)
			setCap(result.cap)
			setCandidatePage(page)
			setCandidateTotal(result.total)
			setEditing(true)
		} catch (e: any) {
			setError(e.message)
		} finally {
			setBusy(false)
		}
	}
	async function saveSelection(productIds: string[] | null) {
		setBusy(true)
		setError("")
		try {
			const result = await request(`${base}/stores/${slug}/selection`, "POST", { productIds })
			setState(result.participation)
			setEditing(false)
		} catch (e: any) {
			setError(e.message)
		} finally {
			setBusy(false)
		}
	}
	async function saveLimits() {
		const selectionLimits = { percentage: Number(percentage), maxProducts: maximum.trim() ? Number(maximum) : null }
		if (
			!percentage.trim() ||
			!Number.isFinite(selectionLimits.percentage) ||
			selectionLimits.percentage < 1 ||
			selectionLimits.percentage > 100 ||
			(selectionLimits.maxProducts !== null &&
				(!Number.isSafeInteger(selectionLimits.maxProducts) || selectionLimits.maxProducts < 1))
		) {
			setError("Enter a percentage from 1 to 100 and a positive whole product limit, or leave the maximum blank.")
			return
		}
		setBusy(true)
		setError("")
		try {
			const result = await request(`${base}/stores/${slug}`, "PUT", { enabled: state!.enabled, selectionLimits })
			setState(result.participation)
			limitsDirty.current = false
			setPreview([])
			setEditing(false)
		} catch (e: any) {
			setError(e.message)
		} finally {
			setBusy(false)
		}
	}
	return (
		<section className="bg-card border border-border rounded-lg p-6 mb-6">
			<h2 className="text-lg font-semibold">Shortlist by Shopin5</h2>
			<p className="text-sm text-muted-foreground mb-3">
				Include this store in ChatGPT discovery. Choose how much of its catalogue participates using Shopify’s
				best-selling order or your saved manual selection. Sold-out products remain eligible.
			</p>
			<label className="flex items-center gap-2">
				<input
					checked={state?.enabled || false}
					disabled={busy || !slug}
					onChange={() => void act("toggle")}
					type="checkbox"
				/>
				Use in Shortlist / ChatGPT
			</label>
			<label className="flex items-center gap-2 mt-2">
				<input
					checked={state?.crossBrandDiscovery || false}
					disabled={busy || !slug || !state?.enabled}
					onChange={() => void act("discovery")}
					type="checkbox"
				/>
				Show in cross-brand search on myshortlist.in
			</label>
			<p className="text-xs text-muted-foreground mt-1 ml-6">
				Shoppers searching across brands see this store's Shortlist products next to other labels, ranked by fit. Your
				own brand page and widget never show other brands.
			</p>
			<form
				className="mt-4"
				onSubmit={(event) => {
					event.preventDefault()
					void saveLimits()
				}}>
				<fieldset disabled={busy || !slug || !state} className="flex flex-wrap items-end gap-3">
					<legend className="font-medium mb-2">Catalogue limits for this brand</legend>
					<label className="flex flex-col gap-1">
						Catalogue percentage
						<input
							aria-label="Catalogue percentage"
							type="number"
							min="1"
							max="100"
							step="any"
							required
							value={percentage}
							onChange={(event) => {
								limitsDirty.current = true
								setPercentage(event.target.value)
							}}
							className="border rounded-md p-2 w-28"
						/>
					</label>
					<label className="flex flex-col gap-1">
						Maximum products (optional)
						<input
							aria-label="Maximum products"
							type="number"
							min="1"
							step="1"
							value={maximum}
							placeholder="No maximum"
							onChange={(event) => {
								limitsDirty.current = true
								setMaximum(event.target.value)
							}}
							className="border rounded-md p-2 w-40"
						/>
					</label>
					<button type="submit" className="px-3 py-2 border rounded-md disabled:opacity-50">
						Save catalogue limits
					</button>
				</fieldset>
				<p className="text-sm text-muted-foreground mt-2">
					The smaller limit applies. For the full catalogue, use 100% and leave the maximum blank. Saving refreshes an
					enabled brand. Manual selections keep their chosen products.
				</p>
			</form>
			{state && (
				<p className="text-sm mt-3" role="status">
					{state.status} · {state.selectedCount || 0} selected
					{state.catalogueCount !== undefined ? ` from ${state.catalogueCount} products` : ""}
					{state.lastRefreshedAt ? ` · Updated ${new Date(state.lastRefreshedAt).toLocaleString()}` : ""}
				</p>
			)}
			<p className="text-sm mt-2">
				Selection source: {state?.manualProductIds ? "Manual curation" : "Shopify best-selling order"}
			</p>
			{(error || state?.error) && (
				<p className="text-sm text-red-600 mt-2" role="alert">
					{error || state?.error}
				</p>
			)}
			{state?.status === "error" && (
				<p className="text-sm text-muted-foreground mt-2">
					{state.enabled && state.activeGeneration
						? "The last successful selection is still available in Shortlist. This refresh did not replace it."
						: "This store has no active selection and is unavailable in Shortlist. Its existing merchant search is unaffected."}
				</p>
			)}
			{!storeUrl && <p className="text-sm mt-2">Register/publish the store before enabling Shortlist.</p>}
			<div className="flex gap-2 mt-3">
				<button
					className="px-3 py-2 border rounded-md disabled:opacity-50"
					disabled={busy || !slug}
					onClick={() => void loadCandidates()}>
					Choose products manually
				</button>
				<button
					className="px-3 py-2 border rounded-md disabled:opacity-50"
					disabled={busy || !slug || !state?.enabled}
					onClick={() => void act("refresh-catalogue")}>
					Refresh catalogue + retry Shortlist
				</button>
				<button
					className="px-3 py-2 border rounded-md disabled:opacity-50"
					disabled={busy || !slug}
					onClick={() => void act("preview")}>
					Preview selection
				</button>
				<button
					className="px-3 py-2 border rounded-md disabled:opacity-50"
					disabled={busy || !state?.enabled}
					onClick={() => void act("refresh")}>
					Refresh selection
				</button>
			</div>
			{editing && (
				<div className="mt-4 border rounded-md p-3">
					<p className="text-sm">
						Choose up to {cap} products. {manualIds.length} selected. Nightly refresh preserves these IDs; missing
						products require your review.
					</p>
					<div className="flex flex-wrap gap-2 my-2">
						{manualIds.map((id) => (
							<button
								disabled={busy}
								key={id}
								onClick={() => setManualIds((ids) => ids.filter((value) => value !== id))}>
								Remove selected {id} ×
							</button>
						))}
					</div>
					<form
						className="flex gap-2 my-2"
						onSubmit={(e) => {
							e.preventDefault()
							void loadCandidates(1)
						}}>
						<input
							aria-label="Find catalogue products"
							className="border rounded px-2"
							onChange={(e) => setCandidateQuery(e.target.value)}
							value={candidateQuery}
						/>
						<button disabled={busy}>Find products</button>
					</form>
					{candidates.map((p) => (
						<label className="flex gap-2 text-sm py-1" key={p.id}>
							<input
								checked={manualIds.includes(p.id)}
								disabled={
									busy ||
									(!manualIds.includes(p.id) &&
										(manualIds.length >= cap ||
											(p.gpt_readiness !== undefined && p.gpt_readiness.status !== "ready")))
								}
								onChange={(e) =>
									setManualIds((ids) => (e.target.checked ? [...ids, p.id] : ids.filter((id) => id !== p.id)))
								}
								type="checkbox"
							/>
							{p.title} · {p.id}
							{p.gpt_readiness && p.gpt_readiness.status !== "ready" && (
								<span
									role="status"
									className="text-amber-700"
									title={p.gpt_readiness.reason || "Review required"}>
									⚠ {p.gpt_readiness.status === "excluded" ? "Excluded" : "Needs review"}:{" "}
									{p.gpt_readiness.reason} ({p.gpt_readiness.description_bytes} bytes). Excluded from Shortlist
									/ GPT.
									{p.url && (
										<a href={p.url} target="_blank" rel="noreferrer" className="underline ml-2">
											Review source product
										</a>
									)}
								</span>
							)}
						</label>
					))}
					{!candidates.length && <p>No matching indexed products. Refresh the catalogue if products are missing.</p>}
					<div className="flex flex-wrap gap-3 mt-3">
						<button disabled={busy || candidatePage <= 1} onClick={() => void loadCandidates(candidatePage - 1)}>
							Previous
						</button>
						<button
							disabled={busy || candidatePage * 50 >= candidateTotal}
							onClick={() => void loadCandidates(candidatePage + 1)}>
							Next
						</button>
						<button
							disabled={busy || !manualIds.length || manualIds.length > cap}
							onClick={() => void saveSelection(manualIds)}>
							Save manual selection
						</button>
						<button disabled={busy} onClick={() => setEditing(false)}>
							Cancel
						</button>
					</div>
				</div>
			)}
			{state?.manualProductIds && (
				<button className="px-3 py-2 border rounded-md mt-3" disabled={busy} onClick={() => void saveSelection(null)}>
					Switch back to best-selling selection
				</button>
			)}
			{preview.length > 0 && (
				<ol className="list-decimal ml-5 mt-3 text-sm">
					{preview.map((p) => (
						<li key={p.rank}>{p.title}</li>
					))}
				</ol>
			)}
			{slug && <ShortlistJourneySettings key={`journeys-${slug}`} slug={slug} request={request} />}
			{slug && <ShortlistLaunchLinkSettings key={slug} slug={slug} request={request} />}
		</section>
	)
}
